import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardBody, Button, Typography } from '@material-tailwind/react';
import { FaEnvelope, FaFileCsv, FaFilePdf, FaFilter, FaSyncAlt, FaCar, FaMoneyBillWave, FaBoxOpen, FaUser, FaWallet, FaChartLine } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { rootUrl } from '../../../config/config';
import { useAuth } from '../AuthContext';
import usePermissions from '../../common/usePermissions';
import ReportLink from './shared/ReportLink';
import { downloadCsv, formatCurrency, formatDate, formatDateTime, rangeLabels } from './shared/reportUtils';
import { downloadElementAsPdf } from './shared/reportExport';

const RANGE_OPTIONS = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
  { value: 'all_time', label: 'All time' }
];

const REPORT_META = {
  vehicle: {
    title: 'Vehicle Drilldown',
    permission: 'reports.drilldown.vehicle.view',
    label: 'Vehicle',
    routeBase: '/reports/drilldowns/vehicle'
  },
  transaction: {
    title: 'Transaction Drilldown',
    permission: 'reports.drilldown.transaction.view',
    label: 'Transaction',
    routeBase: '/reports/drilldowns/transaction'
  },
  expense: {
    title: 'Expense Drilldown',
    permission: 'reports.drilldown.expense.view',
    label: 'Expense',
    routeBase: '/reports/drilldowns/expense'
  },
  'stock-item': {
    title: 'Stock Item Drilldown',
    permission: 'reports.drilldown.stock_item.view',
    label: 'Stock Item',
    routeBase: '/reports/drilldowns/stock-item'
  },
  owner: {
    title: 'Owner Drilldown',
    permission: 'reports.drilldown.owner.view',
    label: 'Owner',
    routeBase: '/reports/drilldowns/owner'
  },
  'finance-account': {
    title: 'Finance Account Drilldown',
    permission: 'reports.drilldown.finance_account.view',
    label: 'Finance Account',
    routeBase: '/reports/drilldowns/finance-account'
  }
};

const StatCard = ({ title, value, icon: Icon, tone = 'text-slate-900' }) => (
  <Card className="border border-[#eadfd8] shadow-sm rounded-2xl bg-white">
    <CardBody className="p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{title}</p>
          <div className={`mt-2 text-2xl font-black ${tone}`}>{value}</div>
        </div>
        {Icon ? <div className="rounded-xl bg-[#fff4ef] p-3 text-[#FF5733]"><Icon /></div> : null}
      </div>
    </CardBody>
  </Card>
);

const SectionCard = ({ title, children }) => (
  <Card className="border border-[#eadfd8] shadow-sm rounded-2xl bg-white">
    <CardBody className="p-5">
      <Typography className="mb-4 text-lg font-bold text-[#2a2e30]">{title}</Typography>
      {children}
    </CardBody>
  </Card>
);

const ReportActions = ({ onPdf, onCsv, onEmail, pdfDisabled, csvDisabled, emailDisabled }) => (
  <div className="flex flex-wrap items-center gap-2">
    <button type="button" onClick={onPdf} disabled={pdfDisabled} className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-orange-300 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-50">
      <FaFilePdf />
      PDF
    </button>
    <button type="button" onClick={onCsv} disabled={csvDisabled} className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-orange-300 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-50">
      <FaFileCsv />
      CSV
    </button>
    <button type="button" onClick={onEmail} disabled={emailDisabled} className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-orange-300 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-50">
      <FaEnvelope />
      Send now
    </button>
  </div>
);

const EmailModal = ({ open, onClose, onSend, loading, defaultSubject, defaultMessage }) => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [subject, setSubject] = useState(defaultSubject || '');
  const [message, setMessage] = useState(defaultMessage || '');

  useEffect(() => {
    if (!open) return;
    setRecipientEmail('');
    setSubject(defaultSubject || '');
    setMessage(defaultMessage || '');
  }, [open, defaultMessage, defaultSubject]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
      <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
        <Typography className="text-xl font-bold text-[#2a2e30]">Send report now</Typography>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">Recipient email</span>
            <input value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} className="w-full rounded-xl border border-slate-300 px-4 py-2 outline-none focus:border-[#FF5733]" placeholder="name@example.com" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">Subject</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full rounded-xl border border-slate-300 px-4 py-2 outline-none focus:border-[#FF5733]" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">Message</span>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} className="w-full rounded-xl border border-slate-300 px-4 py-2 outline-none focus:border-[#FF5733]" />
          </label>
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">Cancel</button>
          <button
            type="button"
            disabled={loading || !recipientEmail.trim()}
            onClick={() => onSend({ recipient_email: recipientEmail.trim(), subject: subject.trim(), message: message.trim() })}
            className="rounded-full bg-[#FF5733] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReportDrilldownPage = ({ type }) => {
  const meta = REPORT_META[type];
  const { id } = useParams();
  const { token } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const contentRef = useRef(null);
  const [draftRange, setDraftRange] = useState('week');
  const [appliedRange, setAppliedRange] = useState('week');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token || localStorage.getItem('authToken') || 'default-token'}`
  }), [token]);

  const allowed = hasPermission(meta?.permission);

  const fetchReport = async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${rootUrl}/api/reports/drilldowns/${type}/${encodeURIComponent(id)}`, {
        headers: authHeaders,
        params: { range: draftRange }
      });
      setReport(response.data?.data || null);
      setAppliedRange(draftRange);
    } catch (err) {
      setReport(null);
      setError(err.response?.data?.message || err.message || 'Failed to load drilldown');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!permissionsLoading && allowed) {
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, id, permissionsLoading]);

  const onApply = async () => {
    await fetchReport();
  };

  const onExportPdf = async () => {
    if (!contentRef.current) return;
    await downloadElementAsPdf(contentRef.current, {
      filename: `${meta?.routeBase.split('/').pop()}-${id}-${appliedRange}.pdf`,
      reportTitle: 'Reports',
      sectionTitle: `${meta?.title || 'Drilldown'}`,
      subtitle: rangeLabels[appliedRange] || appliedRange
    });
  };

  const buildCsv = () => {
    if (type === 'vehicle') {
      return {
        headers: ['Date', 'Type', 'Debit', 'Credit', 'Amount', 'Reference'],
        rows: (report?.rows || []).map((row) => [row.date, row.type, row.debit, row.credit, row.amount, row.your_ref || row.our_ref || row.id]),
        filename: `vehicle-drilldown-${id}.csv`
      };
    }
    if (type === 'transaction') {
      const entity = report?.entity || {};
      return {
        headers: ['Field', 'Value'],
        rows: Object.entries(entity).map(([key, value]) => [key, value]),
        filename: `transaction-drilldown-${id}.csv`
      };
    }
    if (type === 'expense') {
      const entity = report?.entity || {};
      return {
        headers: ['Field', 'Value'],
        rows: Object.entries(entity).map(([key, value]) => [key, value]),
        filename: `expense-drilldown-${id}.csv`
      };
    }
    if (type === 'stock-item') {
      return {
        headers: ['Date', 'Type', 'Change', 'Before', 'After', 'Reference'],
        rows: (report?.rows || []).map((row) => [row.date, row.trans_type, row.quantity_change, row.quantity_before, row.quantity_after, row.id]),
        filename: `stock-item-drilldown-${id}.csv`
      };
    }
    if (type === 'owner') {
      return {
        headers: ['Date', 'Type', 'Plate No', 'Debit', 'Credit', 'Amount'],
        rows: (report?.rows || []).map((row) => [row.date, row.type, row.plate_no, row.debit, row.credit, row.amount]),
        filename: `owner-drilldown-${id}.csv`
      };
    }
    return {
      headers: ['Date', 'Type', 'Amount', 'Description'],
      rows: (report?.rows || []).map((row) => [row.entry_date, row.entry_type, row.amount, row.description]),
      filename: `finance-account-drilldown-${id}.csv`
    };
  };

  const onExportCsv = () => {
    const csv = buildCsv();
    downloadCsv(csv.headers, csv.rows, csv.filename);
  };

  const onSendNow = async ({ recipient_email, subject, message }) => {
    setEmailLoading(true);
    try {
      await axios.post(`${rootUrl}/api/reports/send-now`, {
        report_type: type,
        report_title: meta?.title || type,
        recipient_email,
        subject,
        message,
        summary: report?.entity || undefined
      }, { headers: authHeaders });
      setEmailOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to send drilldown email');
    } finally {
      setEmailLoading(false);
    }
  };

  const entity = useMemo(() => report?.entity || {}, [report?.entity]);

  const summaryCards = useMemo(() => {
    if (type === 'vehicle') {
      return [
        { title: 'Money In', value: formatCurrency((report?.rows || []).reduce((sum, row) => sum + Number(row.debit || 0), 0)), icon: FaMoneyBillWave, tone: 'text-emerald-600' },
        { title: 'Money Out', value: formatCurrency((report?.rows || []).reduce((sum, row) => sum + Number(row.credit || 0), 0)), icon: FaMoneyBillWave, tone: 'text-rose-600' },
        { title: 'Net', value: formatCurrency((report?.rows || []).reduce((sum, row) => sum + (Number(row.debit || 0) - Number(row.credit || 0)), 0)), icon: FaChartLine, tone: 'text-[#FF5733]' },
        { title: 'Transactions', value: (report?.rows || []).length, icon: FaCar, tone: 'text-slate-900' }
      ];
    }
    if (type === 'stock-item') {
      return [
        { title: 'Quantity', value: entity.quantity || 0, icon: FaBoxOpen, tone: 'text-slate-900' },
        { title: 'Cost', value: formatCurrency(entity.cost_incl || entity.cost_excl), icon: FaBoxOpen, tone: 'text-emerald-600' },
        { title: 'Price', value: formatCurrency(entity.price_incl || entity.price_excl), icon: FaBoxOpen, tone: 'text-sky-600' },
        { title: 'Value', value: formatCurrency((entity.quantity || 0) * Number(entity.cost_incl || entity.cost_excl || 0)), icon: FaBoxOpen, tone: 'text-[#FF5733]' }
      ];
    }
    if (type === 'owner') {
      return [
        { title: 'Vehicles', value: (report?.vehicles || []).length, icon: FaCar, tone: 'text-slate-900' },
        { title: 'Transactions', value: (report?.rows || []).length, icon: FaMoneyBillWave, tone: 'text-emerald-600' },
        { title: 'Owner', value: entity.code || id, icon: FaUser, tone: 'text-sky-600' }
      ];
    }
    if (type === 'finance-account') {
      return [
        { title: 'Entries', value: (report?.rows || []).length, icon: FaWallet, tone: 'text-slate-900' },
        { title: 'Account', value: entity.account_name || id, icon: FaWallet, tone: 'text-emerald-600' },
        { title: 'Balance', value: formatCurrency(entity.current_balance || 0), icon: FaWallet, tone: 'text-[#FF5733]' }
      ];
    }
    return [
      { title: 'Rows', value: (report?.rows || []).length, icon: FaMoneyBillWave, tone: 'text-slate-900' }
    ];
  }, [entity, id, report?.rows, report?.vehicles, type]);

  if (!allowed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-8">
          <Typography className="text-2xl font-bold text-[#2a2e30]">Access denied</Typography>
          <p className="mt-2 text-sm text-slate-600">You do not have permission to open this drilldown report.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <Typography className="text-4xl font-black text-[#2a2e30]">{meta?.title || 'Drilldown'}</Typography>
          <p className="mt-2 text-sm text-slate-600">Dedicated drilldown report for {meta?.label || type}. Opened from the main Reports hub.</p>
        </div>

        <Card className="border border-[#eadfd8] shadow-sm rounded-2xl bg-white">
          <CardBody className="p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center gap-2">
                  <FaFilter className="text-[#FF5733]" />
                  <span className="text-sm font-semibold text-slate-700">Range</span>
                </div>
                <select value={draftRange} onChange={(e) => setDraftRange(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm outline-none focus:border-[#FF5733]">
                  {RANGE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <Button onClick={onApply} className="inline-flex items-center gap-2 bg-[#FF5733] text-white shadow-none hover:bg-[#e64c22]">
                  <FaSyncAlt />
                  Apply
                </Button>
              </div>
              <ReportActions
                onPdf={onExportPdf}
                onCsv={onExportCsv}
                onEmail={() => setEmailOpen(true)}
                pdfDisabled={loading}
                csvDisabled={loading}
                emailDisabled={loading}
              />
            </div>
          </CardBody>
        </Card>

        {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

        <div ref={contentRef} className="space-y-6">
          {loading ? (
            <div className="rounded-2xl border border-[#eadfd8] bg-white p-10 text-center shadow-sm">
              <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-[#FF5733]" />
              <p className="text-sm text-slate-600">Loading {meta?.title || 'drilldown'}...</p>
            </div>
          ) : null}

          {!loading ? (
            <>
              <div
                className="grid gap-4 md:grid-cols-2"
                style={{ gridTemplateColumns: `repeat(${Math.max(summaryCards.length, 1)}, minmax(0, 1fr))` }}
              >
                {summaryCards.map((card) => (
                  <StatCard key={card.title} {...card} />
                ))}
              </div>

              {type === 'vehicle' ? (
                <SectionCard title={`Vehicle ${entity.plate_no || id}`}>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Owner</p>
                      <p className="mt-1 text-sm font-semibold text-slate-800">{entity.owner_name || entity.owner_code || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Driver</p>
                      <p className="mt-1 text-sm font-semibold text-slate-800">{entity.driver_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Status</p>
                      <p className="mt-1 text-sm font-semibold text-slate-800">{entity.status_name || '-'}</p>
                    </div>
                  </div>
                  <div className="mt-6 overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="px-3 py-2">Date</th>
                          <th className="px-3 py-2">Type</th>
                          <th className="px-3 py-2 text-right">Debit</th>
                          <th className="px-3 py-2 text-right">Credit</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                          <th className="px-3 py-2">Open</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(report?.rows || []).map((row) => (
                          <tr key={row.id} className="border-b border-slate-100">
                            <td className="px-3 py-3 text-slate-700">{formatDate(row.date)}</td>
                            <td className="px-3 py-3 text-slate-700">{row.type}</td>
                            <td className="px-3 py-3 text-right text-emerald-600">{formatCurrency(row.debit)}</td>
                            <td className="px-3 py-3 text-right text-rose-600">{formatCurrency(row.credit)}</td>
                            <td className="px-3 py-3 text-right font-semibold text-slate-900">{formatCurrency(row.amount)}</td>
                            <td className="px-3 py-3">
                              <ReportLink to={`/reports/drilldowns/transaction/${encodeURIComponent(row.id)}`} newTab>
                                Open
                              </ReportLink>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              ) : null}

              {type === 'transaction' ? (
                <SectionCard title={`Transaction ${entity.id || id}`}>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <SummaryField label="Plate No" value={entity.plate_no} link={entity.plate_no ? `/reports/drilldowns/vehicle/${encodeURIComponent(entity.plate_no)}` : null} />
                    <SummaryField label="Type" value={entity.type} />
                    <SummaryField label="Amount" value={formatCurrency(entity.amount)} />
                    <SummaryField label="Date" value={formatDateTime(entity.date)} />
                    <SummaryField label="Owner" value={entity.owner_code} />
                    <SummaryField label="Reference" value={entity.your_ref || entity.our_ref || entity.doc_no || entity.id} />
                  </div>
                </SectionCard>
              ) : null}

              {type === 'expense' ? (
                <SectionCard title={`Expense ${entity.expense_no || entity.id || id}`}>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <SummaryField label="Description" value={entity.description} />
                    <SummaryField label="Amount" value={formatCurrency(entity.amount)} />
                    <SummaryField label="Owner" value={entity.owner_name || entity.owner_code} />
                    <SummaryField label="Cashbook Account" value={entity.cashbook_account_name} />
                    <SummaryField label="Approval" value={entity.approval_status} />
                    <SummaryField label="Date" value={formatDate(entity.expense_date || entity.date)} />
                  </div>
                </SectionCard>
              ) : null}

              {type === 'stock-item' ? (
                <SectionCard title={`Stock Item ${entity.part_name || entity.part_id || id}`}>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <SummaryField label="Part Number" value={entity.part_number} />
                    <SummaryField label="Manufacturer" value={entity.manufacturer} />
                    <SummaryField label="Quantity" value={entity.quantity} />
                    <SummaryField label="Cost" value={formatCurrency(entity.cost_incl || entity.cost_excl)} />
                    <SummaryField label="Price" value={formatCurrency(entity.price_incl || entity.price_excl)} />
                    <SummaryField label="Category" value={entity.category} />
                  </div>
                  <div className="mt-6 overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="px-3 py-2">Date</th>
                          <th className="px-3 py-2">Type</th>
                          <th className="px-3 py-2 text-right">Change</th>
                          <th className="px-3 py-2 text-right">Before</th>
                          <th className="px-3 py-2 text-right">After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(report?.rows || []).map((row) => (
                          <tr key={row.id} className="border-b border-slate-100">
                            <td className="px-3 py-3 text-slate-700">{formatDate(row.date)}</td>
                            <td className="px-3 py-3 text-slate-700">{row.trans_type}</td>
                            <td className="px-3 py-3 text-right text-slate-900">{row.quantity_change}</td>
                            <td className="px-3 py-3 text-right text-slate-900">{row.quantity_before}</td>
                            <td className="px-3 py-3 text-right text-slate-900">{row.quantity_after}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              ) : null}

              {type === 'owner' ? (
                <>
                  <SectionCard title={`Owner ${entity.code || id}`}>
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                      <SummaryField label="Name" value={entity.name} />
                      <SummaryField label="Phone" value={entity.phone} />
                      <SummaryField label="Email" value={entity.email} />
                    </div>
                  </SectionCard>
                  <SectionCard title="Owner Vehicles">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-left text-sm">
                        <thead className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                          <tr>
                            <th className="px-3 py-2">Vehicle</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Open</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(report?.vehicles || []).map((row) => (
                            <tr key={row.plate_no} className="border-b border-slate-100">
                              <td className="px-3 py-3 font-semibold text-slate-700">{row.plate_no}</td>
                              <td className="px-3 py-3 text-slate-700">{row.status_name || '-'}</td>
                              <td className="px-3 py-3">
                                <ReportLink to={`/reports/drilldowns/vehicle/${encodeURIComponent(row.plate_no)}`} newTab>
                                  Open
                                </ReportLink>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </SectionCard>
                  <SectionCard title="Recent Owner Transactions">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-left text-sm">
                        <thead className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                          <tr>
                            <th className="px-3 py-2">Date</th>
                            <th className="px-3 py-2">Vehicle</th>
                            <th className="px-3 py-2">Type</th>
                            <th className="px-3 py-2 text-right">Amount</th>
                            <th className="px-3 py-2">Open</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(report?.rows || []).map((row) => (
                            <tr key={row.id} className="border-b border-slate-100">
                              <td className="px-3 py-3 text-slate-700">{formatDate(row.date)}</td>
                              <td className="px-3 py-3 text-slate-700">{row.plate_no}</td>
                              <td className="px-3 py-3 text-slate-700">{row.type}</td>
                              <td className="px-3 py-3 text-right text-slate-900">{formatCurrency(row.amount)}</td>
                              <td className="px-3 py-3">
                                <ReportLink to={`/reports/drilldowns/transaction/${encodeURIComponent(row.id)}`} newTab>
                                  Open
                                </ReportLink>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </SectionCard>
                </>
              ) : null}

              {type === 'finance-account' ? (
                <>
                  <SectionCard title={`Finance Account ${entity.account_name || id}`}>
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                      <SummaryField label="Type" value={entity.account_type} />
                      <SummaryField label="Bank" value={entity.bank_name} />
                      <SummaryField label="Balance" value={formatCurrency(entity.current_balance || 0)} />
                      <SummaryField label="Account No" value={entity.bank_account_number} />
                      <SummaryField label="Phone" value={entity.mpesa_phone} />
                    </div>
                  </SectionCard>
                  <SectionCard title="Account Entries">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-left text-sm">
                        <thead className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                          <tr>
                            <th className="px-3 py-2">Date</th>
                            <th className="px-3 py-2">Type</th>
                            <th className="px-3 py-2">Description</th>
                            <th className="px-3 py-2 text-right">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(report?.rows || []).map((row) => (
                            <tr key={row.id} className="border-b border-slate-100">
                              <td className="px-3 py-3 text-slate-700">{formatDate(row.entry_date)}</td>
                              <td className="px-3 py-3 text-slate-700">{row.entry_type}</td>
                              <td className="px-3 py-3 text-slate-700">{row.description}</td>
                              <td className="px-3 py-3 text-right text-slate-900">{formatCurrency(row.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </SectionCard>
                </>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      <EmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        onSend={onSendNow}
        loading={emailLoading}
        defaultSubject={`${meta?.title || 'Drilldown'}: ${id}`}
        defaultMessage={`Attached summary for ${meta?.label || type} ${id} (${rangeLabels[appliedRange] || appliedRange}).`}
      />
    </div>
  );
};

const SummaryField = ({ label, value, link }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
    {link ? (
      <ReportLink to={link} newTab className="mt-1 inline-block text-sm">
        {value || '-'}
      </ReportLink>
    ) : (
      <p className="mt-1 text-sm font-semibold text-slate-800">{value || '-'}</p>
    )}
  </div>
);

export default ReportDrilldownPage;
